import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { 
  Building2, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Users,
  CreditCard,
  CheckCircle,
  XCircle,
  Eye,
  Clock,
  AlertTriangle,
  X
} from "lucide-react";
const AgencyDetailsModal = ({ agency, onClose, onApprove, onReject }) => {
  const [loading, setLoading] = useState(false);

  if (!agency) return null;

  // Prevent background scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return "N/A";
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return { 
          icon: <Clock className="w-4 h-4" />, 
          text: 'In Attesa di Approvazione',
          color: 'bg-yellow-100 text-yellow-700 border-yellow-200'
        };
      case 'approved':
        return { 
          icon: <CheckCircle className="w-4 h-4" />, 
          text: 'Approvata',
          color: 'bg-green-100 text-green-700 border-green-200'
        };
      case 'rejected':
        return { 
          icon: <XCircle className="w-4 h-4" />, 
          text: 'Rifiutata',
          color: 'bg-red-100 text-red-700 border-red-200'
        };
      default:
        return { 
          icon: <AlertTriangle className="w-4 h-4" />, 
          text: 'Stato Sconosciuto',
          color: 'bg-gray-100 text-gray-700 border-gray-200'
        };
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/admin/agenzie/${agency.id}/approve`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      toast.success(`✅ Agenzia approvata! Credenziali inviate a: ${data.username}`);
      
      if (onApprove) {
        onApprove(agency.id, data);
      }
      
      onClose();
    } catch (error) {
      console.error("Error approving agency:", error);
      toast.error("Errore nell'approvazione agenzia");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/admin/agenzie/${agency.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'rejected' })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      toast.success("Agenzia rifiutata");
      
      if (onReject) {
        onReject(agency.id);
      }
      
      onClose();
    } catch (error) {
      console.error("Error rejecting agency:", error);
      toast.error("Errore nel rifiuto agenzia");
    } finally {
      setLoading(false);
    }
  };

  const statusInfo = getStatusInfo(agency.stato_approvazione || agency.stato || 'pending');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" style={{overflow: 'hidden'}}>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100 flex flex-col" style={{minHeight: '400px'}}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                {(agency.nome_agenzia || agency.nome)?.charAt(0) || 'A'}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{agency.nome_agenzia || agency.nome}</h2>
                <Badge className={`${statusInfo.color} border`}>
                  {statusInfo.icon}
                  <span className="ml-1">{statusInfo.text}</span>
                </Badge>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Informazioni Principali */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                Informazioni Agenzia
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium">Città:</span>
                  <span className="text-sm text-gray-600">{agency.citta || 'N/A'} {agency.provincia ? `(${agency.provincia})` : ''}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium">Anni Esperienza:</span>
                  <span className="text-sm text-gray-600">{agency.anni_esperienza || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium">Dipendenti:</span>
                  <span className="text-sm text-gray-600">{agency.numero_dipendenti || 'N/A'}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium">P.IVA:</span>
                  <span className="text-sm text-gray-600">{agency.partita_iva || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium">Data Registrazione:</span>
                  <span className="text-sm text-gray-600">{formatDate(agency.data_registrazione || agency.created_at)}</span>
                </div>
                {agency.sito_web && (
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium">Sito Web:</span>
                    <a href={agency.sito_web} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                      {agency.sito_web}
                    </a>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Contatti */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-green-500" />
                Contatti
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium">Email:</span>
                  <span className="text-sm text-gray-600">{agency.email || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium">Telefono:</span>
                  <span className="text-sm text-gray-600">{agency.telefono || 'N/A'}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium">Indirizzo:</span>
                  <span className="text-sm text-gray-600">{agency.indirizzo || 'N/A'}</span>
                </div>
                {agency.username && (
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium">Username:</span>
                    <span className="text-sm text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded">
                      {agency.username}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Descrizione */}
          {agency.descrizione && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Descrizione Agenzia
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700 bg-blue-50 p-3 rounded-lg border border-blue-200">
                  {agency.descrizione}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Statistiche (se disponibili) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-purple-500" />
                Valutazione
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="font-bold text-lg text-blue-600">
                    {agency.citta ? '✅' : '❌'}
                  </div>
                  <div className="text-xs text-gray-600">Città Specificata</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="font-bold text-lg text-green-600">
                    {agency.email ? '✅' : '❌'}
                  </div>
                  <div className="text-xs text-gray-600">Email Valida</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="font-bold text-lg text-purple-600">
                    {agency.telefono ? '✅' : '❌'}
                  </div>
                  <div className="text-xs text-gray-600">Telefono Fornito</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        {(agency.stato_approvazione || agency.stato || 'pending') === 'pending' && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
            <div className="flex gap-3 justify-end">
              <Button 
                variant="outline" 
                onClick={onClose}
                disabled={loading}
              >
                Chiudi
              </Button>
              <Button 
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={handleReject}
                disabled={loading}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Rifiuta Agenzia
              </Button>
              <Button 
                className="bg-green-500 hover:bg-green-600 text-white"
                onClick={handleApprove}
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Approvazione...
                  </div>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Approva Agenzia
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgencyDetailsModal;